import React, { useContext, useState, useEffect, createContext } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to approve user if email is verified and email matches licensee
  const approveUserIfEligible = async (user) => {
    console.log('Starting approveUserIfEligible function...');
    console.log('User object:', user);

    if (user.emailVerified) {
      console.log('Email is verified for user:', user.email);

      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('User data from Firestore:', userData);

        // Parking customers are a separate vertical — never licensee-match
        // them by email (created approved by completeParkingSignup, so this
        // path shouldn't run for them; guard is defence-in-depth).
        if ((userData.role || 'user') === 'parking') {
          console.log('Parking account — skipping licensee auto-approval.');
          return;
        }

        // Check if user is already approved
        if (userData.status === 'approved') {
          console.log('User is already approved. No further action needed.');
          return;
        }

        // Search for matching licensee by email
        console.log('Searching for matching licensee by email:', user.email.toLowerCase());
        const licenseeQuery = query(
          collection(db, 'licensees'),
          where('EMAIL', '==', user.email.toLowerCase())
        );
        const licenseeSnapshot = await getDocs(licenseeQuery);

        if (!licenseeSnapshot.empty) {
          console.log('Matching licensee found!');
          const licenseeDoc = licenseeSnapshot.docs[0].data();
          console.log('Licensee data:', licenseeDoc);

          const referenceCode = licenseeDoc['REFERENCE CODE'];
          console.log('Assigning reference code to user:', referenceCode);

          // Update user document with reference code and approve the user
          await updateDoc(userRef, {
            referenceCode,
            status: 'approved',
          });

          console.log('User document updated with reference code and status set to approved.');

          setUserStatus('approved');
          setUserRole('user');
          console.log('User status and role updated in local state.');
        } else {
          console.log('No matching licensee found for email:', user.email.toLowerCase());
        }
      } else {
        console.log('No user document found in Firestore for UID:', user.uid);
      }
    } else {
      console.log('Email is not verified for user:', user.email);
    }

    console.log('approveUserIfEligible function completed.');
  };

  useEffect(() => {
    let unsubscribeUserDoc = null; // Declare unsubscribe function
    let intervalId = null; // Declare interval ID for periodic checks
  
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      console.log('onAuthStateChanged triggered. User:', user);
  
      if (user) {
        setCurrentUser(user);
  
        const userRef = doc(db, 'users', user.uid);
  
        // Check if user document exists and create/fix if needed
        try {
          const userDoc = await getDoc(userRef);
          if (!userDoc.exists()) {
            console.log('User document does not exist. Creating one.');

            const isStaffAccount = user.email && user.email.endsWith('@lobi.ie');

            // Staff accounts should NOT be auto-created - something is very wrong
            if (isStaffAccount) {
              console.error('🚨 CRITICAL: Staff account missing user document!');
              console.error('   Email:', user.email);
              console.error('   This should never happen - staff accounts must be created manually');
              console.error('   NOT creating document automatically');
              // Don't create document, let staff monitoring catch this
              return;
            }

            // Defence-in-depth: if this email was rejected during approval, do
            // NOT auto-create a stub doc. The proper fix is to also delete the
            // Auth account on reject (so we never get here at all). This guard
            // covers any orphan Auth accounts left from before that fix landed.
            // Without this, signing back in would resurrect a "NO NAME /
            // pending / No invite match" stub in Portal Account Manager.
            try {
              const rejectedSnap = await getDocs(
                query(
                  collection(db, 'user_deletions'),
                  where('userEmail', '==', (user.email || '').toLowerCase().trim()),
                  where('reason', '==', 'rejected_during_approval')
                )
              );
              if (!rejectedSnap.empty) {
                console.warn('🚫 Refusing to recreate user doc — email was rejected during approval');
                console.warn('   Email:', user.email);
                console.warn('   Rejection records:', rejectedSnap.size);
                console.warn('   This Auth account is orphaned from a prior rejection.');
                console.warn('   Signing out client-side. Staff can fully delete from Portal Account Manager.');
                try {
                  await addDoc(collection(db, 'audit_logs'), {
                    timestamp: serverTimestamp(),
                    operation: 'rejected_user_recovery_blocked',
                    collection: 'users',
                    documentId: user.uid,
                    performedBy: 'auth-context-recovery',
                    details: {
                      email: user.email,
                      reason: 'email_previously_rejected_during_approval',
                      rejectionRecordCount: rejectedSnap.size,
                      trigger: 'login_with_missing_document',
                    },
                  });
                } catch (auditError) {
                  console.error('Failed to write block audit log:', auditError);
                }
                // Sign out so the orphan Auth account cannot continue to a
                // recovery-created stub doc on a subsequent state change.
                try {
                  await signOut(auth);
                } catch (signOutErr) {
                  console.error('Sign out after rejection-block failed:', signOutErr);
                }
                return;
              }
            } catch (rejectionCheckErr) {
              // If the rejection check itself fails, fall through to the
              // existing recovery path — better an over-eager stub than locking
              // a legitimate user out.
              console.error('Rejection check failed (continuing recovery):', rejectionCheckErr);
            }

            // Create audit log for new user document creation
            try {
              await addDoc(collection(db, 'audit_logs'), {
                timestamp: serverTimestamp(),
                operation: 'user_document_created',
                collection: 'users',
                documentId: user.uid,
                performedBy: 'auth-context-recovery',
                details: {
                  email: user.email,
                  reason: 'user_document_missing',
                  createdData: {
                    email: user.email,
                    emailVerified: user.emailVerified,
                    status: 'pending',
                    role: 'user'
                  },
                  trigger: 'login_with_missing_document'
                }
              });
            } catch (auditError) {
              console.error('Failed to create audit log for new user document:', auditError);
            }

            // Create user document (only for non-staff)
            await setDoc(userRef, {
              email: user.email,
              emailVerified: user.emailVerified,
              status: 'pending',
              role: 'user',
              createdAt: new Date(),
              createdBy: 'auth-context-recovery'
            });
            console.log('User document created successfully');
          } else {
            const userData = userDoc.data();
            console.log('User document exists:', userData);

            // Publish role/status from this first read so pages can start
            // loading now. Same defaults as the listener below, which still
            // takes over for live changes.
            setUserRole(userData.role || 'user');
            setUserStatus(userData.status || 'pending');

            // Check if document is missing critical fields and fix
            // IMPORTANT: Only set defaults if fields are truly missing (undefined/null)
            // Do NOT overwrite existing values like 'approved' or 'admin'
            // CRITICAL: Do NOT auto-fix @lobi.ie staff accounts - they need manual intervention
            const isStaffAccount = user.email && user.email.endsWith('@lobi.ie');
            const needsUpdate = {};
            if (!userData.email) needsUpdate.email = user.email;

            // Skip role/status recovery for staff accounts - manual fix required
            if (!isStaffAccount) {
              if (userData.role === undefined || userData.role === null) needsUpdate.role = 'user';
              if (userData.status === undefined || userData.status === null) needsUpdate.status = 'pending';
            } else if ((userData.role === undefined || userData.role === null) ||
                       (userData.status === undefined || userData.status === null)) {
              // Staff account with missing fields - log but don't fix automatically
              console.error('🚨 STAFF ACCOUNT CORRUPTION DETECTED:', user.email);
              console.error('   Role:', userData.role, '| Status:', userData.status);
              console.error('   MANUAL FIX REQUIRED - Not auto-setting to "user"');
            }

            if (Object.keys(needsUpdate).length > 0) {
              console.log('Fixing incomplete user document with:', needsUpdate);

              // Create audit log BEFORE making changes
              try {
                await addDoc(collection(db, 'audit_logs'), {
                  timestamp: serverTimestamp(),
                  operation: 'user_document_recovery',
                  collection: 'users',
                  documentId: user.uid,
                  performedBy: 'auth-context-recovery',
                  details: {
                    email: user.email,
                    fieldsFixed: Object.keys(needsUpdate),
                    updates: needsUpdate,
                    previousData: userData,
                    trigger: 'missing_fields_detected'
                  }
                });
              } catch (auditError) {
                console.error('Failed to create audit log for recovery:', auditError);
                // Continue with recovery even if audit logging fails
              }

              await updateDoc(userRef, {
                ...needsUpdate,
                lastFixed: new Date(),
                fixedBy: 'auth-context-recovery'
              });
            }
          }
        } catch (docError) {
          console.error('Error handling user document:', docError);

          const isStaffAccount = user.email && user.email.endsWith('@lobi.ie');

          // Staff accounts should NOT be auto-created even in error scenarios
          if (isStaffAccount) {
            console.error('🚨 CRITICAL: Staff account error during recovery!');
            console.error('   Email:', user.email);
            console.error('   Error:', docError.message);
            console.error('   NOT creating fallback document - manual intervention required');
            return;
          }

          // Try to create a basic document as fallback (non-staff only)
          try {
            // Create audit log for fallback document creation
            try {
              await addDoc(collection(db, 'audit_logs'), {
                timestamp: serverTimestamp(),
                operation: 'user_document_fallback',
                collection: 'users',
                documentId: user.uid,
                performedBy: 'auth-context-fallback',
                details: {
                  email: user.email,
                  reason: 'error_during_document_recovery',
                  originalError: docError.message,
                  fallbackData: {
                    email: user.email,
                    emailVerified: user.emailVerified,
                    status: 'pending',
                    role: 'user'
                  },
                  trigger: 'exception_handler'
                }
              });
            } catch (auditError) {
              console.error('Failed to create audit log for fallback:', auditError);
            }

            await setDoc(userRef, {
              email: user.email,
              emailVerified: user.emailVerified,
              status: 'pending',
              role: 'user',
              createdAt: new Date(),
              createdBy: 'auth-context-fallback'
            }, { merge: true });
            console.log('Fallback user document created');
          } catch (fallbackError) {
            console.error('Even fallback document creation failed:', fallbackError);
          }
        }
  
        // Set up Firestore listener for the user's document
        unsubscribeUserDoc = onSnapshot(
          userRef,
          async (docSnapshot) => {
            console.log('onSnapshot triggered for user document:', docSnapshot.data());
            const userData = docSnapshot.data();
            if (userData) {
              setUserRole(userData.role || 'user');
              setUserStatus(userData.status || 'pending');
            } else {
              console.warn('User document snapshot is empty, setting defaults');
              setUserRole('user');
              setUserStatus('pending');
  
            }

            // If user status is 'pending', set up periodic email verification check
            const currentStatus = userData?.status || 'pending';
            if (currentStatus === 'pending' && !intervalId) {
                console.log('Setting up periodic email verification check for pending user.');
  
                intervalId = setInterval(async () => {
                  console.log('Running periodic email verification check...');
                  await user.reload(); // Reload the user's authentication data
                  const updatedUser = auth.currentUser; // Get the updated user object
  
                  console.log('Updated emailVerified status:', updatedUser.emailVerified);
  
                  // If the email is verified, update Firestore and approve the user
                  if (updatedUser.emailVerified) {
                    console.log('Email verified. Attempting to approve user...');
                    await updateDoc(userRef, { emailVerified: true });
                    await approveUserIfEligible(updatedUser);
                    clearInterval(intervalId); // Stop further checks
                    intervalId = null; // Reset interval ID
                  }
                }, 20000); // Run the check every 30 seconds
              }
          },
          (error) => {
            console.error('Error in onSnapshot:', error);
          }
        );
  
      } else {
        setCurrentUser(null);
        setUserRole(null);
        setUserStatus(null);
        if (unsubscribeUserDoc) {
          unsubscribeUserDoc(); // Unsubscribe from user doc listener
          unsubscribeUserDoc = null; // Reset the unsubscribe function
        }
        if (intervalId) {
          clearInterval(intervalId); // Clear the periodic check
          intervalId = null; // Reset interval ID
        }
      }
      setLoading(false);
    });
  
    // Cleanup function for the useEffect hook
    return () => {
      unsubscribeAuth(); // Unsubscribe from onAuthStateChanged
      if (unsubscribeUserDoc) {
        unsubscribeUserDoc(); // Unsubscribe from onSnapshot
      }
      if (intervalId) {
        clearInterval(intervalId); // Clear the periodic check
      }
    };
  }, []);
  
  // Function to handle signup
  const signup = async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create minimal user document in Firestore
    const userRef = doc(db, 'users', user.uid);
    
    // Check if document already exists (shouldn't happen, but be defensive)
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        email: user.email,
        emailVerified: user.emailVerified,
        status: 'pending',
        role: 'user',
        createdAt: new Date(),
        authCreated: true, // Flag to indicate this was created during auth signup
      });
      console.log('Created minimal user document during signup');
    } else {
      console.warn('User document already exists during signup - this is unexpected');
    }

    return user;
  };

  // Function to handle login
  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential;
  };

  // Function to handle password reset
  const resetPassword = async (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Function to handle logout
  const logout = () => {
    return signOut(auth);
  };

  // Auth context value passed to the components
  const value = {
    currentUser,
    userRole,
    userStatus,
    signup,
    login,
    logout,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}
